import { Box, type SxProps, type Theme } from "@mui/material";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import type { DateTime } from "luxon";
import { type FC, type ReactNode, useCallback, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthProvider";
import { localStorageService } from "@/services/LocalStorageService";
import { MOTION } from "@/theme/tokens";
import SystemButton from "../SystemButton/SystemButton";
import { InlineHint, type InlineHintTone } from "./InlineHint";

/** The bounds of one announcement campaign — who it is for, and until when. */
export type OneTimeNoticeCampaign = {
	/**
	 * Hard end of the campaign: nothing is eligible once `DateTime.now()` is past this. Required rather
	 * than optional because an announcement with no end date is a permanent fixture nobody removes.
	 */
	expiresAt: DateTime;
	/**
	 * Announce a *change* only to the people who lived through the old behavior: accounts created at
	 * or after this instant are never eligible. Omit for a notice that is for everyone.
	 */
	onlyForAccountsCreatedBefore?: DateTime;
	/** Fired once, when the user acknowledges — for analytics. Persistence is handled by the hook. */
	onDismiss?: () => void;
};

export type OneTimeNoticeState = {
	/**
	 * Whether this user should still be told. Gate **everything** the campaign owns on this single
	 * value — the note itself *and* any affordance that reveals it — so a trigger can never survive
	 * its own notice and open onto nothing.
	 */
	isEligible: boolean;
	/** Persist the acknowledgement, flip `isEligible` to false, and notify `onDismiss`. */
	dismiss: () => void;
};

/**
 * The whole "should this person still be told, and have they said yes" question in one place: the
 * per-user acknowledgement (persisted via `localStorageService`), an optional account-age gate, and
 * a hard expiry.
 *
 * It is a hook rather than logic inside `OneTimeNotice` because a notice is not always rendered
 * where it is *reached from*: park it behind a header icon and the icon needs the same three answers
 * the note does. One caller, one `useOneTimeNotice`, shared by both — never the date/localStorage
 * rules re-derived beside the trigger, which is how the two drift apart.
 */
export const useOneTimeNotice = (noticeKey: string, { expiresAt, onlyForAccountsCreatedBefore, onDismiss }: OneTimeNoticeCampaign): OneTimeNoticeState => {
	const { userInfo } = useAuth();
	// Read once on mount: re-reading every render would let an unrelated write flip the notice away
	// mid-interaction, and the value only ever changes through `dismiss` below.
	const [isAcknowledged, setIsAcknowledged] = useState(() => localStorageService.getOneTimeNoticeDismissed(noticeKey, userInfo.id));

	const dismiss = useCallback(() => {
		localStorageService.setOneTimeNoticeDismissed(noticeKey, userInfo.id);
		setIsAcknowledged(true);
		onDismiss?.();
	}, [noticeKey, userInfo.id, onDismiss]);

	const isExpired = expiresAt.diffNow().toMillis() <= 0;
	// An account whose creation date we cannot read is not provably older than the change, so it does
	// not qualify. Failing closed here is deliberate: the cost of staying silent is one uninformed
	// user, the cost of guessing wrong is telling a brand-new user that "their" numbers moved.
	const predatesChange = onlyForAccountsCreatedBefore ? !!userInfo.created_at?.isValid && userInfo.created_at < onlyForAccountsCreatedBefore : true;

	return useMemo(() => ({ isEligible: !isAcknowledged && !isExpired && predatesChange, dismiss }), [isAcknowledged, isExpired, predatesChange, dismiss]);
};

type PresentationProps = {
	/** The message, already translated — the caller owns i18n (same contract as `InlineHint`). */
	children: ReactNode;
	/** Label of the acknowledge button, already translated (e.g. "Understood", "Got it"). */
	dismissLabel: string;
	/** A call to action before the acknowledge button; the caller decides whether it also counts as seen. */
	// DEFAULT: absent = the acknowledge button alone.
	leadingAction?: ReactNode;
	tone?: InlineHintTone;
	/** Override the leading glyph so the notice reads as its meaning. */
	icon?: ReactNode;
	/** Where the acknowledge button sits — see `InlineHint`. Narrow surfaces want `"below"`. */
	actionPlacement?: "inline" | "below";
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

type ContentProps = PresentationProps & {
	/** `false` folds the note away — the acknowledge gesture. Owned by whoever holds the eligibility. */
	isVisible: boolean;
	onAcknowledge: () => void;
};

/**
 * The note itself, with no opinion on who may see it — for a host that already owns the eligibility
 * (see `useOneTimeNotice`) because it also gates something else with it, e.g. `OneTimeNoticePopover`
 * and its trigger icon. Everything else should reach for `OneTimeNotice` below, which wires the two
 * together itself.
 */
export const OneTimeNoticeContent: FC<ContentProps> = ({
	isVisible,
	onAcknowledge,
	children,
	dismissLabel,
	leadingAction,
	tone,
	icon,
	actionPlacement,
	dataTestId,
	sx,
}) => {
	const prefersReducedMotion = useReducedMotion();
	// `noFullWidthMobile`: an inline acknowledgement hugs its label instead of taking 85% of a phone column.
	const acknowledgeButton = (
		<SystemButton
			buttonVariant="borderless"
			size="small"
			noFullWidthMobile
			onClick={onAcknowledge}
			data-testid={dataTestId ? `${dataTestId}-dismiss` : undefined}
		>
			{dismissLabel}
		</SystemButton>
	);

	return (
		<AnimatePresence>
			{isVisible && (
				<motion.div
					// No enter animation — `InlineHint` already fades itself in, and the notice is present on
					// first paint anyway. The exit is the moment worth animating: acknowledging should feel
					// like the note folding away, not a jump cut. Snappier than an entrance (asymmetric
					// timing), and height is animated only because collapsing the gap it leaves behind is
					// the whole point of the gesture.
					initial={false}
					exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, height: 0, transform: "translateY(-4px)" }}
					transition={{ duration: (prefersReducedMotion ? MOTION.DURATION.OVERLAY_FADE : MOTION.DURATION.WINDOW_EXIT) / 1000, ease: MOTION.EASE_OUT_POINTS }}
					// A notice is a guest in someone else's layout: it must adopt the container's width, never
					// set it. `width: 0` keeps its long sentence out of the parent's max-content calculation
					// (otherwise dropping this into a content-sized surface — a popover, a menu — stretches
					// that surface to one unbroken line), while `minWidth: 100%` renders it at full width.
					style={{ overflow: "hidden", width: 0, minWidth: "100%" }}
				>
					<InlineHint
						dataTestId={dataTestId}
						tone={tone}
						icon={icon}
						actionPlacement={actionPlacement}
						sx={sx}
						action={
							leadingAction ? (
								<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
									{leadingAction}
									{acknowledgeButton}
								</Box>
							) : (
								acknowledgeButton
							)
						}
					>
						{children}
					</InlineHint>
				</motion.div>
			)}
		</AnimatePresence>
	);
};

type Props = PresentationProps &
	OneTimeNoticeCampaign & {
		/**
		 * Stable id for this notice. The acknowledgement is persisted per user under it, so changing it
		 * re-shows the notice to everyone — treat it as part of the campaign, not a cosmetic name.
		 */
		noticeKey: string;
	};

/**
 * A notice that is shown **once**, to the **right** people, for a **bounded** time — the "we changed
 * something you had already gotten used to" announcement, parked next to the control that undoes it.
 *
 * Owns the whole eligibility question so a campaign can never half-implement it: the per-user
 * acknowledgement (persisted via `localStorageService`), an optional account-age gate, and a hard
 * expiry. Everything else is the caller's: copy, tone, glyph, and where it sits — which is also what
 * keeps the component reusable for the next rollout rather than welded to one message.
 *
 * Renders an `InlineHint`, so it reads as one family with every other inline note in the app.
 * When the note should sit behind an affordance rather than in the layout, use
 * `OneTimeNoticePopover` — the same campaign, revealed by an icon that disappears with it.
 * For an always-on caveat about a surface use `SystemBanner`; for a multi-step walkthrough,
 * `SystemTutorial`.
 */
export const OneTimeNotice: FC<Props> = ({ noticeKey, expiresAt, onlyForAccountsCreatedBefore, onDismiss, ...presentation }) => {
	const notice = useOneTimeNotice(noticeKey, { expiresAt, onlyForAccountsCreatedBefore, onDismiss });

	return <OneTimeNoticeContent isVisible={notice.isEligible} onAcknowledge={notice.dismiss} {...presentation} />;
};
